// src/pages/Admin/Views/SellerManagement/EditCoinsModal.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { api } from '../../../services/apiServices';

// Import shadcn/ui Dialog components [cite: 24]
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../../components/ui/dialog";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Textarea } from "../../../components/ui/textarea";
import { Alert, AlertDescription } from "../../../components/ui/alert"; // [cite: 25, 26]

// --- Business Logic Constants ---
const DEFAULT_PER_COIN_VALUE = process.env.REACT_APP_BASE_COIN_RATE;
const MAX_DISCOUNT_PERCENTAGE = 50;
const WARNING_DISCOUNT_PERCENTAGE = 30;

const EditCoinsModal = ({ isOpen, sellerId, currentBalance, onClose, onUpdateSuccess }) => {
  const [changeAmount, setChangeAmount] = useState('');
  const [valueExTax, setValueExTax] = useState('');
  const [reason, setReason] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Derived state calculated from inputs
  const { perCoinValue, discount, isInvalid } = useMemo(() => {
    const amount = parseInt(changeAmount, 10);
    const value = parseFloat(valueExTax);

    if (isNaN(amount) || isNaN(value) || amount <= 0) {
      return { perCoinValue: 0, discount: 0, isInvalid: false };
    }

    const calculatedPerCoinValue = value / amount;
    const calculatedDiscount = ((DEFAULT_PER_COIN_VALUE - calculatedPerCoinValue) / DEFAULT_PER_COIN_VALUE) * 100;
    
    return {
        perCoinValue: calculatedPerCoinValue,
        discount: calculatedDiscount,
        isInvalid: calculatedDiscount > MAX_DISCOUNT_PERCENTAGE,
    };
  }, [changeAmount, valueExTax]);

  // Reset state when the modal is closed/opened
  useEffect(() => {
    if (!isOpen) {
        setChangeAmount('');
        setValueExTax('');
        setReason('');
        setError('');
        setIsLoading(false);
    }
  }, [isOpen]);

  const getDiscountColor = () => {
    if (discount > MAX_DISCOUNT_PERCENTAGE) return 'text-destructive'; // Red 
    if (discount > WARNING_DISCOUNT_PERCENTAGE) return 'text-amber-600'; // Amber
    if (discount >= 0) return 'text-green-600'; // Green
    return 'text-foreground'; // Black for negative discount (premium)
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isInvalid) {
      setError(`Discount cannot exceed ${MAX_DISCOUNT_PERCENTAGE}%.`);
      return;
    }
    // ... other validation from previous steps
    
    setIsLoading(true);
    setError('');

    try {
      const payload = { 
        coin_change: parseInt(changeAmount, 10), 
        reason, 
        value_ex_tax: parseFloat(valueExTax) 
      };
      const response = await api.post(`/admin/sellers/${sellerId}/update-coins`, payload);
      onUpdateSuccess(response.data, { amount: payload.coin_change, reason, value: payload.value_ex_tax });
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update coins.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Edit Coin Balance</DialogTitle>
          <DialogDescription>
            Current Balance: <strong>{currentBalance}</strong>. Add or reduce coins and provide a reason.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label htmlFor="changeAmount">Coins to Add</Label>
                <Input id="changeAmount" type="number" placeholder="e.g., 100" value={changeAmount} onChange={(e) => setChangeAmount(e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="valueExTax">Total Value (Ex. Tax)</Label>
                <Input id="valueExTax" type="number" placeholder="e.g., 2700" value={valueExTax} onChange={(e) => setValueExTax(e.target.value)} />
              </div>
          </div>
          
          {/* Real-time Calculation Display */}
          {(changeAmount && valueExTax) && (
             <Alert variant={isInvalid ? "destructive" : "default"} className="text-sm">
                <AlertDescription className="flex justify-between">
                    <span>Per Coin: ₹{perCoinValue.toFixed(2)}</span>
                    <span className={getDiscountColor()}>
                        Discount: {discount.toFixed(1)}%
                    </span>
                </AlertDescription>
             </Alert>
          )}

          <div className="space-y-1">
            <Label htmlFor="reason">Reason for Change</Label>
            <Textarea id="reason" placeholder="Enter a mandatory reason for this transaction..." value={reason} onChange={(e) => setReason(e.target.value)} />
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}
        
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={isLoading || isInvalid}>
                {isLoading ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditCoinsModal;